import FormFieldComponent from '@/components/FormFieldComponent';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { useAddCheckNumber } from '@/services/checkRegisterService';
import { displayError } from '@/lib/error';
import { toast } from '@/components/ui/use-toast';
import type { CheckRegisterItem } from '@/types/checkRegister.types';
import { z } from 'zod';
import { useEffect } from 'react';
import { Button } from './ui/button';

const AddCheckNumberFormSchema = z.object({
  checkNumber: z.string().min(1, 'Check number is required'),
  amount: z.string().min(1, 'Amount is required'),
  issuedTo: z.string().min(1, 'Issued to is required'),
  issueDate: z.date({ required_error: 'Issue date is required' }),
  reference: z.string().optional(),
});

type AddCheckNumberFormData = z.infer<typeof AddCheckNumberFormSchema>;

interface AddCheckNumberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: CheckRegisterItem | null;
  onSuccess?: () => void;
}

const AddCheckNumberModal = ({ open, onOpenChange, payment, onSuccess }: AddCheckNumberModalProps) => {
  const addCheckNumberMutation = useAddCheckNumber();

  const form = useForm<AddCheckNumberFormData>({
    resolver: zodResolver(AddCheckNumberFormSchema),
    mode: 'onChange',
    defaultValues: {
      checkNumber: undefined,
      amount: '0',
      issuedTo: undefined,
      issueDate: undefined,
      reference: undefined,
    },
  });

  const { control, reset } = form;

  useEffect(() => {
    if (payment && open) {
      reset({
        checkNumber: payment.checkNumber ? String(payment.checkNumber) : '',
        amount: payment.amount?.toFixed(2) ?? '0',
        issuedTo: payment.issuedTo ?? undefined,
        issueDate: payment.issuedDate ? new Date(payment.issuedDate) : undefined,
        reference: payment.reference ?? undefined,
      });
    }
  }, [payment, open, form]);

  const handleSubmit = async (data: AddCheckNumberFormData) => {
    if (!payment) return;

    try {
      await addCheckNumberMutation.mutateAsync({
        paymentId: payment.paymentId,
        data: {
          checkNumber: parseInt(data.checkNumber, 10),
          amount: parseFloat(data.amount),
          issuedTo: data.issuedTo,
          issueDate: data.issueDate,
          reference: data.reference ?? null,
        },
      });

      toast({
        description: 'Check number added successfully',
        duration: 3000,
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (err: unknown) {
      displayError(err as Parameters<typeof displayError>[0]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[50vw] max-h-[75vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{payment?.workOrderNumber ? `W/O: ${payment.workOrderNumber}` : 'Payment'}</DialogTitle>
        </DialogHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="flex flex-col bg-[#F5F5F5] gap-4 px-3 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormFieldCalendar
                  control={control}
                  name={`issueDate`}
                  label="Date"
                  placeholder="Select Date"
                  required
                  disabled={true}
                />
                <FormFieldComponent
                  control={control}
                  name={`reference`}
                  label="Reference"
                  placeholder="Enter Reference"
                  disabled={true}
                />
              </div>
              <div className="grid grid-cols-1 gap-6">
                <FormFieldComponent
                  control={control}
                  name={`issuedTo`}
                  label="Issued To"
                  placeholder="Enter Issued To"
                  required
                  disabled={true}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormFieldComponent
                  control={control}
                  name={`checkNumber`}
                  label="Check #"
                  placeholder="Enter Check Number"
                  type="number"
                  allowDecimals={false}
                />
                <FormFieldComponent
                  control={control}
                  name={`amount`}
                  label="Amount"
                  placeholder="Enter Amount"
                  type="number"
                  required
                  disabled={true}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  className="min-w-[100px]"
                  type="submit"
                  disabled={addCheckNumberMutation.isPending || !form.formState.isValid}
                >
                  Save
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default AddCheckNumberModal;
